const DEFAULT_FALLBACK_BASE = "arquivo";

const stripDiacritics = (value: string) =>
  value.normalize("NFKD").replace(/\p{M}+/gu, "");

// eslint-disable-next-line no-control-regex
const stripControlChars = (value: string) => value.replace(/[\u0000-\u001F\u007F]/g, "");

const collapseDashes = (value: string) =>
  value
    .replace(/-+/g, "-")
    .replace(/^[-.]+/, "")
    .replace(/-+$/g, "");

const collapseWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const splitFilename = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return { base: "", ext: "" };
  if (trimmed.startsWith(".") && trimmed.indexOf(".", 1) === -1) {
    const extOnly = trimmed.slice(1);
    if (extOnly) return { base: "", ext: extOnly };
  }
  const lastDot = trimmed.lastIndexOf(".");
  if (lastDot <= 0) return { base: trimmed, ext: "" };
  return { base: trimmed.slice(0, lastDot), ext: trimmed.slice(lastDot + 1) };
};

/**
 * Sanitiza um nome de arquivo para uso em chaves/caminhos do Storage:
 * - remove acentos (NFKD + diacríticos)
 * - remove caracteres de controle e separadores de caminho
 * - limita a um conjunto seguro (A-Z, a-z, 0-9, ".", "_", "-")
 * - preserva a extensão quando presente
 */
export const sanitizeFilenameForStorage = (
  filename: string,
  opts?: { fallbackBase?: string; maxLength?: number },
) => {
  const fallbackBase = opts?.fallbackBase?.trim() || DEFAULT_FALLBACK_BASE;
  const maxLength = opts?.maxLength ?? 120;

  const cleaned = stripControlChars(filename);
  const { base, ext } = splitFilename(cleaned);

  const baseSafe = collapseDashes(
    stripDiacritics(base)
      .replace(/[\\/]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9._-]+/g, "-"),
  );

  const extSafe = stripDiacritics(ext)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

  const finalBase = (baseSafe || fallbackBase).slice(0, maxLength);
  if (!extSafe) return finalBase;
  return `${finalBase}.${extSafe}`;
};

/**
 * Gera um título amigável a partir do nome do arquivo, removendo caracteres
 * problemáticos (acentos, controles, símbolos) e mantendo legibilidade.
 */
export const sanitizeTitleFromFilename = (filename: string) => {
  const cleaned = stripControlChars(filename);
  const { base } = splitFilename(cleaned);
  const withoutDiacritics = stripDiacritics(base);
  const normalized = withoutDiacritics
    .replace(/[\\/]+/g, " ")
    .replace(/[_-]+/g, " ")
    .replace(/[^a-zA-Z0-9 ]+/g, " ");
  return collapseWhitespace(normalized);
};
